'use client';
import { createContext, useContext, useLayoutEffect, useState } from 'react';

import { Theme, ThemeContextProps, ThemeProviderProps } from '@/types/theme';
import { validateTheme } from '@/lib/validate';

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

const getInitialTheme = (): Theme => {
  if (typeof document !== 'undefined') {
    //  Check localStorage for pre-existing theme first
    const storedTheme = localStorage.getItem('chenook-theme');
    if (storedTheme && validateTheme(storedTheme)) {
      return storedTheme as Theme;
    }
    // If not, check user's system preference
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'chenook-dark';
    } else {
      return 'chenook-light';
    }
  }
  return 'chenook-dark';
};

export const useTheme = (): ThemeContextProps => {
  const themeContext = useContext(ThemeContext);

  if (!themeContext) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }

  return themeContext;
};

export default function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setTheme] = useState(getInitialTheme);

  const toggleTheme = () => {
    const newTheme =
      theme === 'chenook-dark' ? 'chenook-light' : 'chenook-dark';
    setTheme(newTheme);
  };

  useLayoutEffect(() => {
    localStorage.setItem('chenook-theme', theme);
    document.body.setAttribute('data-theme', theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
